import { motion, useReducedMotion } from 'framer-motion'
import { Sparkles } from 'lucide-react'
import type { CSSProperties, ReactNode } from 'react'

/**
 * Xpillar Intelligence, as it looks on the platform: one moving gradient,
 * used for anything that is the AI rather than the form. Ported from
 * xpillar/src/components/ai/intelligence.tsx so Hero and the site match.
 *
 * The gradient drifts across the button forever; a viewer who asked their
 * system for less motion gets it still.
 */
export const AI_GRADIENT = 'linear-gradient(110deg,#3BAFDA 0%,#6366F1 22%,#22D3EE 46%,#A855F7 72%,#3BAFDA 100%)'

const SHEEN_TRANSITION = { duration: 9, repeat: Infinity, ease: 'linear' as const }
const SHEEN_KEYFRAMES = { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }

export const GradientSheen = ({ className = '', style }: { className?: string; style?: CSSProperties }) => {
    const still = useReducedMotion()
    return (
        <motion.span
            aria-hidden
            className={`pointer-events-none absolute inset-0 ${className}`}
            style={{ backgroundImage: AI_GRADIENT, backgroundSize: '300% 100%', ...style }}
            animate={still ? undefined : SHEEN_KEYFRAMES}
            transition={SHEEN_TRANSITION}
        />
    )
}

export const GradientText = ({ children, className = '' }: { children: ReactNode; className?: string }) => {
    const still = useReducedMotion()
    return (
        <motion.span
            className={`bg-clip-text text-transparent ${className}`}
            style={{ backgroundImage: AI_GRADIENT, backgroundSize: '300% 100%' }}
            animate={still ? undefined : SHEEN_KEYFRAMES}
            transition={SHEEN_TRANSITION}>
            {children}
        </motion.span>
    )
}

interface IntelligenceButtonProps {
    onClick: () => void
    children?: ReactNode
    size?: 'sm' | 'md'
    className?: string
    ariaLabel?: string
    icon?: boolean
    /** Gradient behind the label; otherwise a white pill with a gradient glow. */
    filled?: boolean
}

export const INTELLIGENCE_LABEL = 'Xpillar Intelligence'

export const IntelligenceButton = ({ onClick, children = INTELLIGENCE_LABEL, size = 'sm', className = '', ariaLabel, icon = true, filled = false }: IntelligenceButtonProps) => {
    const still = useReducedMotion()
    const small = size === 'sm'
    return (
        <motion.button
            type='button'
            onClick={onClick}
            aria-label={ariaLabel}
            whileHover={still ? undefined : { y: -1 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 26 }}
            className={`group relative inline-flex max-w-full cursor-pointer items-center overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6366F1] focus-visible:ring-offset-2 ${className}`}>
            {filled ? (
                <>
                    <GradientSheen className='rounded-md' />
                    <span className={`relative flex items-center text-white ${small ? 'h-8 gap-1.5 px-3' : 'h-9 gap-2 px-4'}`}>
                        {icon && <Sparkles className={small ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden />}
                        <span className={`truncate font-semibold ${small ? 'text-xs' : 'text-sm'}`}>{children}</span>
                    </span>
                </>
            ) : (
                <>
                    <motion.span
                        aria-hidden
                        className={`pointer-events-none absolute rounded-md blur-md transition-opacity group-hover:opacity-90 ${small ? 'inset-x-1.5 -bottom-1 top-1.5' : 'inset-x-2 -bottom-1.5 top-2'}`}
                        style={{ backgroundImage: AI_GRADIENT, backgroundSize: '300% 100%' }}
                        animate={still ? { opacity: 0.55 } : { opacity: [0.45, 0.7, 0.45], backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <span className={`relative flex items-center rounded-md border border-slate-200/80 bg-white ${small ? 'h-8 gap-1.5 px-3' : 'h-9 gap-2 px-4'}`}>
                        {icon && <Sparkles className={`text-[#6366F1] ${small ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} aria-hidden />}
                        <GradientText className={`truncate font-semibold ${small ? 'text-xs' : 'text-sm'}`}>{children}</GradientText>
                    </span>
                </>
            )}
        </motion.button>
    )
}

/** Says the page is in AI mode: the same moving gradient, nothing to click. */
export const IntelligenceBadge = ({ children = INTELLIGENCE_LABEL, className = '' }: { children?: ReactNode; className?: string }) => (
    <span className={`relative inline-flex items-center overflow-hidden rounded-md ${className}`}>
        <GradientSheen className='rounded-md' />
        <span className='relative flex h-8 items-center gap-1.5 px-3 text-white'>
            <Sparkles className='h-3.5 w-3.5' aria-hidden />
            <span className='truncate text-xs font-semibold'>{children}</span>
        </span>
    </span>
)

/** A moving gradient ring, for the AI choice on the chooser. */
export const GradientRing = ({ className = '' }: { className?: string }) => {
    const still = useReducedMotion()
    return (
        <motion.span
            aria-hidden
            className={`pointer-events-none absolute -inset-px rounded-xl ${className}`}
            style={{
                backgroundImage: AI_GRADIENT,
                backgroundSize: '300% 100%',
                // Paints the border only: the fill is masked out.
                WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                padding: 2,
            }}
            animate={still ? undefined : SHEEN_KEYFRAMES}
            transition={SHEEN_TRANSITION}
        />
    )
}
